'use client'

import { useQuery } from '@apollo/client/react'
import { Box, Tag } from '@chakra-ui/react'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NetworkCardPresentation } from '@/components/molecules/Cards/NetworkCard/NetworkCardPresentation'
import {
  type NetworkLogoFragment,
  NetworkNewWizard_MyCirclesDocument,
} from '@/graphql/api/generated'
import { useObjectUrlForFile } from '@/hooks/useObjectUrlForFile'

import type { NetworkWizardFormValues } from './networkWizardTypes'

/**
 * Step 2 wizard preview: network card with selected communities as if already in the network.
 */
export const NetworkCardWizardCompositionPreview = () => {
  const { t } = useTranslation()
  const { control } = useFormContext<NetworkWizardFormValues>()
  const name = useWatch({ control, name: 'name' }) ?? ''
  const description = useWatch({ control, name: 'description' }) ?? ''
  const logo = useWatch({ control, name: 'logo' })
  const logoFile = logo instanceof File ? logo : null
  const logoPreviewSrc = useObjectUrlForFile(logoFile)
  const managedCircleIris =
    useWatch({ control, name: 'managedCircleIris' }) ?? []
  const externalCircles = useWatch({ control, name: 'externalCircles' }) ?? []

  const { data: myCirclesData } = useQuery(NetworkNewWizard_MyCirclesDocument, {
    variables: { disabled: false, searchTerms: undefined },
  })
  const myCircles = useMemo(
    () =>
      myCirclesData?.myCircles?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => Boolean(n)) ?? [],
    [myCirclesData?.myCircles?.edges]
  )

  const selectedCommunities = useMemo(() => {
    const byId = new Map<
      string,
      { id: string; name: string; logoUrl?: string }
    >()

    const managedSet = new Set(managedCircleIris)
    for (const c of myCircles) {
      if (managedSet.has(c.id)) {
        byId.set(c.id, {
          id: c.id,
          name: c.name,
          logoUrl: c.logo?.contentUrl ?? undefined,
        })
      }
    }

    for (const c of externalCircles) {
      if (!byId.has(c.id)) {
        byId.set(c.id, {
          id: c.id,
          name: c.name,
          logoUrl: c.logoContentUrl,
        })
      }
    }

    return [...byId.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  }, [externalCircles, managedCircleIris, myCircles])

  const displayName =
    name.trim() || t('networks.wizard.preview.namePlaceholder')

  const logoNetwork = useMemo(
    (): NetworkLogoFragment => ({
      __typename: 'Network',
      name: displayName,
    }),
    [displayName]
  )

  const stackedUsers = useMemo(() => {
    if (selectedCommunities.length === 0) {
      return undefined
    }
    return selectedCommunities.slice(0, 3).map((c) => ({
      id: c.id,
      fullname: c.name,
      roles: [] as string[],
      avatar: c.logoUrl ? { contentUrl: c.logoUrl } : undefined,
    }))
  }, [selectedCommunities])

  const totalCount =
    selectedCommunities.length > 0 ? selectedCommunities.length : undefined

  const otherLabel = useMemo(() => {
    const total = selectedCommunities.length
    if (total <= 3) {
      return undefined
    }
    return t('networks.card.communitiesStacked.andXOthers', {
      count: total - 3,
    })
  }, [selectedCommunities.length, t])

  return (
    <Box w="full" maxW="md" mx="auto">
      <NetworkCardPresentation
        title={displayName}
        description={description.trim() || undefined}
        logoNetwork={logoNetwork}
        logoPreviewSrc={logoPreviewSrc}
        headerAddon={
          <Tag.Root size="sm" colorPalette="brand">
            <Tag.Label>{t('networks.wizard.preview.badge')}</Tag.Label>
          </Tag.Root>
        }
        stackedUsers={stackedUsers}
        membershipTotalCount={totalCount}
        membershipOtherLabel={otherLabel}
      />
    </Box>
  )
}
